/** @jsx jsx */
import type { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react';
import { Fragment, forwardRef, useEffect, useMemo, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import type { FileRejection } from 'react-dropzone';
import { Button } from '@balance-web/button';
import { jsx } from '@balance-web/core';
import { buildDataAttributes } from '@balance-web/core';
import type { CSSObject, WithDataAttributeProp } from '@balance-web/core';
import { UploadIcon } from '@balance-web/icon/icons/UploadIcon';
import { Stack } from '@balance-web/stack';
import { Text } from '@balance-web/text';
import { useTheme } from '@balance-web/theme';
import { useForkedRef } from '@balance-web/utils';
import { Portal } from '@balance-web/portal';

type FileInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'title'>;

export type FileRejectionProps = FileRejection;

export type FileUploadProps = WithDataAttributeProp<
  {
    title?: ReactNode;
    description?: ReactNode;
    dropZoneHeight?: string | number;
    dropZonePaddingTop?: string | number;
    dropZonePaddingBottom?: string | number;
    inlineDropZone?: boolean;
    windowDropZone?: boolean;
    children?: ReactNode;
    onFilesSelected: (files: File[]) => void;
    onFilesRejected?: (fileRejections: FileRejection[]) => void;
  } & FileInputProps
>;

let windowDropZoneCounter = 0;

export const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>(
  (
    {
      title,
      description,
      inlineDropZone,
      windowDropZone,
      dropZoneHeight,
      dropZonePaddingTop,
      dropZonePaddingBottom,
      disabled,
      onFilesSelected,
      onFilesRejected,
      children,
      multiple = true,
      data,
      ...inputProps
    },
    forwardedRef
  ) => {
    const { palette, radii, elevation, primitives } = useTheme();
    const internalRef = useRef<HTMLInputElement>(null);
    const forkedRef = useForkedRef(internalRef, forwardedRef);

    const rootElementRef = useRef<HTMLDivElement>(null);

    const windowDropZoneOverlayElementRef = useRef<HTMLDivElement | null>(null);

    const formattedAcceptAttribute = (inputProps.accept || '')
      .split(',')
      .reduce((prev, curr) => {
        return { ...prev, ...{ [curr]: [] } };
      }, {});

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      // @ts-ignore Merging HTMLInput props with react-dropzone is causing type collision
      onDrop: (acceptedFiles) => {
        onFilesSelected(acceptedFiles);
        rootElementRef.current?.scrollIntoView({
          behavior: 'smooth',
        });
      },
      onDropRejected: (fileRejections) => {
        rootElementRef.current?.scrollIntoView({
          behavior: 'smooth',
        });
        if (typeof onFilesRejected !== 'function') {
          return;
        }
        onFilesRejected(fileRejections);
      },
      ...inputProps,
      disabled,
      multiple,
      accept: formattedAcceptAttribute,
    });

    const dropZoneStyles: CSSObject = useMemo(() => {
      return {
        width: '100%',
        minHeight: dropZoneHeight || '92px',
        background: palette.background.dialog,
        border: `1px solid ${
          isDragActive && !disabled
            ? palette.text.active
            : palette.border.standard
        }`,
        borderRadius: radii.medium,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        ':hover': {
          background: palette.background.dim,
        },
        ':active': {
          borderStyle: disabled ? undefined : 'solid',
        },
        paddingTop: dropZonePaddingTop ?? '16px',
        paddingBottom: dropZonePaddingBottom ?? '16px',
      };
    }, [
      disabled,
      isDragActive,
      dropZoneHeight,
      dropZonePaddingTop,
      dropZonePaddingBottom,
      palette.background.dialog,
      palette.background.dim,
      palette.border.standard,
      palette.text.active,
      radii.medium,
    ]);

    const dropZoneProps = inlineDropZone
      ? {
          ...getRootProps(),
          css: dropZoneStyles,
        }
      : undefined;

    const internalInputProps = inlineDropZone
      ? { ...getInputProps() }
      : {
          ...inputProps,
          multiple,
          onChange: (event: ChangeEvent<HTMLInputElement>) => {
            onFilesSelected(Array.from(event.target.files || []));
          },
        };

    const handleClick = () => {
      internalRef.current?.click();
    };

    useEffect(() => {
      if (!windowDropZone) {
        return;
      }

      const controller = new AbortController();

      const showOverlay = () => {
        if (windowDropZoneOverlayElementRef.current) {
          windowDropZoneOverlayElementRef.current.style.display = 'flex';
        }
      };

      const hideOverlay = () => {
        if (windowDropZoneOverlayElementRef.current) {
          windowDropZoneOverlayElementRef.current.style.display = 'none';
        }
      };

      document.body.addEventListener(
        'dragenter',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          showOverlay();
        },
        {
          signal: controller.signal,
        }
      );

      document.body.addEventListener(
        'dragover',
        (e) => {
          e.preventDefault();
        },
        {
          signal: controller.signal,
        }
      );

      document.body.addEventListener(
        'dragleave',
        (e) => {
          e.preventDefault();
          if (e.target === windowDropZoneOverlayElementRef.current) {
            hideOverlay();
          }
        },
        {
          signal: controller.signal,
        }
      );

      document.body.addEventListener(
        'drop',
        (e) => {
          e.preventDefault();

          if (
            e.target instanceof Node &&
            e.target !== windowDropZoneOverlayElementRef.current &&
            !windowDropZoneOverlayElementRef.current?.contains(e.target)
          ) {
            return;
          }

          hideOverlay();
          if (internalRef.current) {
            const clonedEvent = new DragEvent('drop', e);
            internalRef.current.dispatchEvent(clonedEvent);
          }
        },
        {
          signal: controller.signal,
        }
      );

      return () => {
        controller.abort();
      };
    }, [windowDropZone]);

    const dataAttributes = buildDataAttributes(data);

    useEffect(
      function trackWindowDropZoneCounter() {
        if (windowDropZone) {
          windowDropZoneCounter += 1;
        }

        return () => {
          if (windowDropZone) {
            windowDropZoneCounter -= 1;
          }
        };
      },
      [windowDropZone]
    );

    if (windowDropZoneCounter > 1) {
      throw Error(
        'There can only be 1 instance of FileUpload component with windowDropZone=true'
      );
    }

    if (dropZoneProps) {
      dropZoneProps.onClick = handleClick;
    }

    return (
      <Fragment>
        <Stack ref={rootElementRef} gap="large">
          {!inlineDropZone ? (
            typeof title === 'string' && title?.length ? (
              <Text weight="semibold">{title}</Text>
            ) : (
              title
            )
          ) : null}
          <div {...dropZoneProps}>
            {inlineDropZone ? (
              <Stack align="center" flex="1" gap="medium">
                <UploadIcon color={disabled ? 'dim' : 'active'} />
                <Text
                  color={disabled ? 'dim' : 'base'}
                  size="medium"
                  weight="medium"
                >
                  {typeof title === 'string' && title?.length ? (
                    <Text weight="semibold">{title}</Text>
                  ) : (
                    title
                  )}
                </Text>
                {typeof description === 'string' && description.length ? (
                  <Text color="muted" size="xsmall">
                    {description}
                  </Text>
                ) : (
                  description
                )}
              </Stack>
            ) : (
              <Button
                iconBefore={UploadIcon}
                label="Upload file"
                size="small"
                variant="outline"
                onClick={handleClick}
              />
            )}
            <input
              css={{ display: 'none' }}
              type="file"
              {...dataAttributes}
              {...internalInputProps}
              ref={forkedRef}
            />
          </div>

          {children}
        </Stack>
        {windowDropZone ? (
          <Portal>
            <div
              ref={windowDropZoneOverlayElementRef}
              css={[
                {
                  backgroundColor: 'rgba(90, 63, 255, 0.16)',
                  backdropFilter: 'blur(2px)',
                  bottom: 0,
                  left: 0,
                  position: 'fixed',
                  right: 0,
                  top: 0,
                  zIndex: elevation['modal'],

                  alignItems: 'center',
                  display: 'none',
                  justifyContent: 'center',
                },
              ]}
            >
              <Stack
                align="center"
                background="base"
                border="standard"
                borderRadius="xlarge"
                css={{ justifyContent: 'center' }}
                gap="medium"
                minHeight={140}
                minWidth={360}
                padding="xxlarge"
              >
                <UploadIcon color="active" />

                <Text
                  css={{
                    color: primitives.colour.neutral.k50,
                  }}
                >
                  Drop anywhere to upload files
                </Text>

                {typeof description === 'string' && description.length ? (
                  <Text color="muted" size="xsmall">
                    {description}
                  </Text>
                ) : (
                  description
                )}
              </Stack>
            </div>
          </Portal>
        ) : null}
      </Fragment>
    );
  }
);
